import React, { useState } from "react";
import {
  View,
  Text,
  StyleSheet,
  Dimensions,
  Platform,
  Linking,
  useWindowDimensions,
} from "react-native";
import LhButton from "../../../components/LhButton";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import LHOverlay from "../../../components/LHOverlay";
import FontFamily from "../../../constants/fonts";
import { useTheme } from "../../../theme";

// Props for NHOResources component
interface NHOResourcesProps {
  onManageEmployeesClick: () => void;
  testID?: string;
  showFilterButton?: boolean;
  onFilterButtonPress?: () => void;
}

// Training resource links
const trainingResources = [
  {
    name: "Power Industrial Truck",
    url: "https://pepsico.sharepoint.com/:f:/r/teams/PBNAPITTrainers/Shared%20Documents/General/Operator%20Training%20Materials?csf=1&web=1&e=1wbW2C",
  },
  {
    name: "Words",
    url: "https://pepsico.sharepoint.com/:f:/r/teams/VP2/Shared%20Documents/Voice%20Pick/02%20-%20Maintenance/10%20-%20Operator%20Maintenance?csf=1&web=1&e=Vceja7",
  },
  {
    name: "Metrics",
    url: "https://mytableau.mypepsico.com/#/redirect_to_view/217830",
  },
  {
    name: "Zero Zone Safety",
    url: "https://www.mypepsico.com/wps/myportal/web/MyPepsiCo/company/departments-and-functions/pbna-ehs-library/home/!ut/p/z1/tVJdb4IwFP0re_Gx6aUC4iMi6lC2-YECL6TFKmxSEPHr36-YJUuWTLYH-9Ck6TnnnnvvwSH2cSjoKd3SKs0F3cl3EOrRm2HrIwfI-PXZtsHswXKqWl2wJhpe3QDuUNGVngoTWACAqTim0R8boKgaDv_Ch1-OCU38JQ5xWMTpGgcxpW3N6GoICOsglRMFMc4I4oSC3iXANgRqdCyqokpwkBXRmbMozkXFRRXtUhZx0YL8WD7FeVZQcW3Bmhe0rDL5f0BUrNHmKOJ6NIcWFExQxJMDksSSlhKc5Bn_auiO4_B-v6vaYsNImzTuKxAFB9Jk59ukMZibYL4QZ9jvTMAcSBenlJ-xJ_IykymY14ory42mnj2T3B0VWyuRF8cBF__cwAiaypNHlneaEiUjT0rXcrdSllYJSsUmx_7PbWP_tm0JTt_3-9CUsapzdKmw_5hczWWnReZ5mdG-oo-ZcV6MsqjfczkzrheNqZ_ToH6W/dz/d5/L2dBISEvZ0FBIS9nQSEh/",
  },
  {
    name: "Safe Route",
    url: "https://pepsico.sharepoint.com/:b:/t/WarehouseTraining/EdZio2somhBHrbO4nqmVfTsBwcCM66VA_YwGqC_YVS4Kwg?e=YkuNhW",
  },
  {
    name: "Environment, Health & Safety - GTM",
    url: "https://app.schoox.com/3455722/environmental-health-and-safety-new-hire-onboarding-gtm",
  },
  {
    name: "DPS Resources",
    url: "https://pepsico.sharepoint.com/teams/WarehouseTraining/DPS/Forms/AllItems.aspx",
  },
];

const NHOResources: React.FC<NHOResourcesProps> = ({
  testID,
  showFilterButton = false,
  onFilterButtonPress,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDarkTheme = resolvedTheme === "dark";
  // State for modals
  const [isResourcesModalVisible, setIsResourcesModalVisible] = useState(false);
  // Track loading state for links and error messages
  const [loadingLinks, setLoadingLinks] = useState<Record<string, boolean>>({});
  const [linkError, setLinkError] = useState<string | null>(null);

  // Use the useWindowDimensions hook for responsive design
  const { width } = useWindowDimensions();

  // Helper function to check if device is in iPhone layout (excluding iPad)
  const isMobileView = () => {
    // Only target iPhone-sized devices, not iPads
    return (
      (Platform.OS === "ios" && width < 768) ||
      (Platform.OS === "android" && width < 480) ||
      (Platform.OS === "web" && width < 480)
    );
  };

  // Helper function to check if device is a very small mobile device
  const isVerySmallMobileView = () => {
    const windowWidth = Dimensions.get("window").width;
    return (
      (Platform.OS === "ios" || Platform.OS === "android") && windowWidth < 375
    );
  };

  // Determine whether to show button text based on screen size
  const showButtonText = !isVerySmallMobileView();

  // Function to get button text based on platform
  const getButtonText = (fullText: string, shortText: string) => {
    if (Platform.OS === "ios" || Platform.OS === "android") {
      return shortText; // Show short text on iPhone/iPad
    } else if (Platform.OS === "web") {
      return showButtonText ? fullText : ""; // Show full text on web or nothing if very small
    }
    return showButtonText ? fullText : ""; // Default fallback
  };

  // Function to handle resource link click with error handling
  const handleResourceClick = (url: string, name: string) => {
    if (url) {
      setLinkError(null);
      setLoadingLinks((prev) => ({ ...prev, [name]: true }));

      Linking.openURL(url)
        .catch((err) => {
          console.error("An error occurred opening the link:", err);
          setLinkError(`Failed to open "${name}". Please try again later.`);
        })
        .finally(() => {
          setLoadingLinks((prev) => ({ ...prev, [name]: false }));
        });
    }
  };

  return (
    <>
      <View
        style={[
          styles.resourcesCard,
          isDarkTheme && { backgroundColor: colors.background.surface },
        ]}
        testID={testID || "nho-resources"}
      >
        <View
          style={
            isMobileView()
              ? styles.resourcesContainerMobile
              : styles.resourcesContainer
          }
        >
          <View
            style={
              isMobileView()
                ? styles.resourcesLeftContentMobile
                : styles.resourcesLeftContent
            }
          >
            {/* <MaterialIcons name="school" size={24} color="#0066cc" /> */}
            <Text
              style={[
                styles.resourcesText,
                isMobileView() && styles.resourcesTextMobile,
                isDarkTheme && { color: colors.text.primary },
              ]}
            >
              New Hire Resources & Actions
            </Text>
          </View>
          <View
            style={isMobileView() ? styles.buttonRowMobile : styles.buttonRow}
            testID="nho-resources-buttons"
          >
            {/* Filter button for iPhone only */}
            {showFilterButton && isMobileView() && onFilterButtonPress && (
              <LhButton
                text=""
                variant="primary"
                size="small"
                iconTrailing="filter-list"
                iconSize={16}
                onPress={onFilterButtonPress}
                isLoading={false}
                disabled={false}
                style={[
                  styles.filterButton,
                  isDarkTheme && {
                    backgroundColor: colors.background.elevated,
                    borderColor: colors.brand.primary,
                    borderWidth: 1,
                  },
                ]}
                textStyle={
                  isDarkTheme
                    ? { ...styles.filterButtonText, color: colors.brand.primary }
                    : styles.filterButtonText
                }
              />
            )}
            {/* <LhButton
                            text={showButtonText ? "Manage Employees" : ""}
                            variant="primary"
                            size="small"
                            iconTrailing="people-alt"
                            iconSize={isMobileView() ? 16 : 20}
                            onPress={onManageEmployeesClick}
                            isLoading={false}
                            disabled={false}
                            style={showButtonText ? styles.actionButton : styles.iconOnlyButton}
                        /> */}
            <LhButton
              text={getButtonText("Training Resources", "Training")}
              variant="secondary"
              size="small"
              iconTrailing="menu-book"
              iconSize={isMobileView() ? 16 : 20}
              onPress={() => setIsResourcesModalVisible(true)}
              isLoading={false}
              disabled={false}
              style={
                showButtonText ? styles.actionButton : styles.iconOnlyButton
              }
            />
          </View>
        </View>
      </View>
      {/* Training Resources Modal */}
      <LHOverlay
        visible={isResourcesModalVisible}
        onDismiss={() => setIsResourcesModalVisible(false)}
        title="New Hire Training Resources"
        size="medium"
      >
        <View
          style={[
            styles.modalContainer,
            isDarkTheme && { backgroundColor: colors.background.surface },
          ]}
        >
          <View
            style={[
              styles.resourcesList,
              { flexDirection: width >= 768 ? "row" : "column" },
            ]}
          >
            {trainingResources.map((resource, index) => (
              <LhButton
                key={`resource-${index}`}
                text={resource.name}
                variant="secondary"
                size="small"
                iconTrailing={resource.url ? "launch" : "block"}
                iconSize={20}
                onPress={() =>
                  resource.url &&
                  handleResourceClick(resource.url, resource.name)
                }
                isLoading={loadingLinks[resource.name]}
                disabled={!resource.url}
                style={{
                  ...styles.resourceButton,
                  width: width >= 768 ? "48%" : "100%",
                }}
                testID={`resource-button-${index}`}
              />
            ))}
          </View>

          {linkError && (
            <Text
              style={[
                styles.errorText,
                isDarkTheme && { color: colors.text.primary },
              ]}
            >
              {linkError}
            </Text>
          )}

          {/* <View style={styles.modalButtons}>
                        <LhButton
                            text="Close"
                            variant="secondary"
                            size="small"
                            onPress={() => setIsResourcesModalVisible(false)}
                            isLoading={false}
                            disabled={false}
                            style={styles.modalButton}
                            testID="resources-modal-close"
                        />
                    </View> */}
        </View>
      </LHOverlay>
    </>
  );
};

const styles = StyleSheet.create({
  resourcesCard: {
    backgroundColor: "white",
    //borderRadiusRight: 8,
    borderTopEndRadius: 8,
    borderTopLeftRadius: 8,
    marginTop: 16,
    // marginBottom: 16,
    //borderLeftWidth: 4,
    //borderLeftColor: "#0066CC",
    //shadowColor: "#000",
    //shadowOffset: { width: 0, height: 1 },
    //shadowOpacity: 0.1,
    //shadowRadius: 3,
    elevation: 2,
    paddingTop:25
  },
  resourcesContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    padding: 16,
    flexWrap: "nowrap",
  },
  resourcesContainerMobile: {
    flexDirection: "column",
    alignItems: "flex-start",
    padding: 10,
  },
  resourcesLeftContent: {
    flexDirection: "row",
    alignItems: "center",
    flexShrink: 1, // Allow this to shrink if needed
  },
  resourcesLeftContentMobile: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    marginBottom: 10,
    width: "100%",
  },
  resourcesText: {
    fontSize: 24,
    fontWeight: "700",
    color: "#2D2D2D",
    marginLeft: 12,
    flexShrink: 1, // Allow text to shrink if needed
  },
  resourcesTextMobile: {
    fontSize: 18,
  },
  buttonRow: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 10,
    alignItems: "center",
    width: "auto",
    marginTop: 0,
  },
  buttonRowMobile: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-end", // Space evenly between buttons
    alignItems: "flex-end",
    width: "100%",
    marginTop: 0,
    gap: 5, // Reduce gap for iPhone to fit three buttons
  },
  actionButton: {
    width:
      Dimensions.get("window").width < 400
        ? 100 // Increased width to prevent text trimming
        : Platform.OS === "web"
        ? "100%"
        : 120,
    //minWidth: Platform.OS === "web" ? 120 : 60,
    fontSize: Dimensions.get("window").width < 400 ? 11 : 16, // Slightly increased font
  },
  iconOnlyButton: {
    width: 50,
    minWidth: 50,
    aspectRatio: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  filterButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "white",
    paddingHorizontal: 4, // Reduced from 12 to compensate for icon's marginLeft: 8
    paddingVertical: 6,
    borderRadius: 24,
    gap: 4,
    width: "auto",
    minWidth: 50,
    paddingRight: 15,
    marginRight: 170,
  },
  filterButtonText: {
    fontSize: 14,
    fontWeight: "500",
    color: "#034EA2",
    fontFamily: FontFamily.regular,
    alignItems: "center",
  },
  modalContainer: {
    padding: 20,
  },
  resourcesList: {
    flexDirection:
      Platform.OS === "web" && Dimensions.get("window").width >= 768
        ? "row"
        : "column",
    flexWrap: "wrap",
    gap: 16,
    justifyContent:
      Platform.OS === "web" && Dimensions.get("window").width >= 768
        ? "space-between"
        : "flex-start",
    // marginBottom: 24,
  },
  resourceButton: {
    width:
      Platform.OS === "web" && Dimensions.get("window").width >= 768
        ? "48%"
        : "100%",
    fontSize: 16,
    marginBottom: 8,
  },
  modalButtons: {
    flexDirection: "row",
    justifyContent: "flex-end",
    marginTop: 20,
    gap: 10,
  },
  modalButton: {
    width: Platform.OS === "web" ? "30%" : "60%",
    fontSize: 16,
  },
  errorText: {
    color: "red",
    fontSize: 14,
    marginTop: 8,
    textAlign: "center",
  },
  overlayContainer: {
    padding: 0,
  },
});

export default NHOResources;
